/**
 * WebSocket client for multi-device real-time sync.
 * Auto-reconnects with exponential backoff.
 */
import { getToken } from './api';

export type RealtimeHandler = (event: {
  type: string;
  payload: unknown;
}) => void;

type StatusHandler = (status: 'connecting' | 'open' | 'closed') => void;

let socket: WebSocket | null = null;
let handlers = new Set<RealtimeHandler>();
let statusHandlers = new Set<StatusHandler>();
let reconnectAttempt = 0;
let intentionalClose = false;
let pingTimer: ReturnType<typeof setInterval> | null = null;

function wsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const token = getToken();
  const device = localStorage.getItem('fink_device') || detectDevice();
  const q = new URLSearchParams();
  if (token) q.set('token', token);
  q.set('device', device);
  return `${proto}//${window.location.host}/ws?${q}`;
}

function detectDevice(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.get('kiosk') === '1' || params.get('tv') === '1') return 'tv-kiosk';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w > 1600) return 'tv';
  return 'desktop';
}

function setStatus(s: 'connecting' | 'open' | 'closed') {
  statusHandlers.forEach((h) => h(s));
}

export function connectRealtime(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  intentionalClose = false;
  setStatus('connecting');
  try {
    socket = new WebSocket(wsUrl());
  } catch {
    scheduleReconnect();
    return;
  }

  socket.onopen = () => {
    reconnectAttempt = 0;
    setStatus('open');
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);
  };

  socket.onmessage = (ev) => {
    try {
      const data = JSON.parse(String(ev.data));
      if (data?.type) {
        handlers.forEach((h) => h(data));
      }
    } catch {
      /* ignore */
    }
  };

  socket.onclose = () => {
    setStatus('closed');
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
    socket = null;
    if (!intentionalClose) scheduleReconnect();
  };

  socket.onerror = () => {
    socket?.close();
  };
}

function scheduleReconnect() {
  const delay = Math.min(1000 * 2 ** reconnectAttempt, 15000);
  reconnectAttempt += 1;
  setTimeout(() => {
    if (!intentionalClose) connectRealtime();
  }, delay);
}

export function disconnectRealtime(): void {
  intentionalClose = true;
  if (pingTimer) clearInterval(pingTimer);
  socket?.close();
  socket = null;
}

export function onRealtime(handler: RealtimeHandler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}

export function onRealtimeStatus(handler: StatusHandler): () => void {
  statusHandlers.add(handler);
  return () => statusHandlers.delete(handler);
}

/**
 * Device label for websocket / analytics.
 * Widget layout is ALWAYS the shared "default" profile so phones and PC
 * show the same customizable dashboard (old per-device layouts caused phones
 * to stick on outdated widget grids).
 */
export function getDeviceProfile(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.get('kiosk') === '1' || params.get('tv') === '1') return 'tv';
  if (window.innerWidth < 768) return 'mobile';
  return 'desktop';
}

/** Shared layout profile for the whole family (all devices). */
export function getLayoutProfile(): string {
  // Allow override: ?layout=mobile keeps a private layout if ever needed
  const params = new URLSearchParams(window.location.search);
  return params.get('layout') || 'default';
}
